/**
 * useAttendanceSettings.js
 * ------------------------
 * Hook managing Student Attendance singleton configuration.
 * Connects directly to FastAPI backend /attendance/settings with zero mock data.
 */
import { useState, useEffect, useCallback } from 'react'
import api from '../../../../api/client'
import { appToast } from '../../../../components/AppToast'

const DEFAULT_SETTINGS = {
  studentSwipes: 'M', // 'M' = Manual, 'A' = Automatic
  absenteeAlerts: 'M', // 'M' = Manual, 'A' = Automatic
  absenteeSmsTo: { father: true, mother: false },
  absenteeWhatsAppTo: { father: true, mother: false },
  misAbsenteeDays: 3,
  autoSmsIn: false,
  autoSmsOut: false,
  autoSmsTo: { father: true, mother: false },
  autoWhatsAppTo: { father: true, mother: false },
  autoSmsMinGap: 5,
}

function backendToUi(data) {
  if (!data) return DEFAULT_SETTINGS
  const smsPref = data.attendance_sms_preference ?? 1
  const waPref = data.attendance_whatsapp_preference ?? 1
  const autoSmsPref = data.auto_sms_preference ?? 1
  const autoWaPref = data.auto_whatsapp_preference ?? 1

  return {
    studentSwipes: data.attendance_student_swipes || 'M',
    absenteeAlerts: data.attendance_absentee_alerts || 'M',
    absenteeSmsTo: {
      father: (smsPref & 1) !== 0,
      mother: (smsPref & 2) !== 0,
    },
    absenteeWhatsAppTo: {
      father: (waPref & 1) !== 0,
      mother: (waPref & 2) !== 0,
    },
    misAbsenteeDays: data.mis_absentee_days ?? 3,
    autoSmsIn: Boolean(data.auto_sms_in),
    autoSmsOut: Boolean(data.auto_sms_out),
    autoSmsTo: {
      father: (autoSmsPref & 1) !== 0,
      mother: (autoSmsPref & 2) !== 0,
    },
    autoWhatsAppTo: {
      father: (autoWaPref & 1) !== 0,
      mother: (autoWaPref & 2) !== 0,
    },
    autoSmsMinGap: data.auto_sms_min_gap ?? 5,
  }
}

function uiToBackend(ui) {
  const smsPref = (ui.absenteeSmsTo.father ? 1 : 0) | (ui.absenteeSmsTo.mother ? 2 : 0)
  const waPref = (ui.absenteeWhatsAppTo.father ? 1 : 0) | (ui.absenteeWhatsAppTo.mother ? 2 : 0)
  const autoSmsPref = (ui.autoSmsTo.father ? 1 : 0) | (ui.autoSmsTo.mother ? 2 : 0)
  const autoWaPref = (ui.autoWhatsAppTo.father ? 1 : 0) | (ui.autoWhatsAppTo.mother ? 2 : 0)

  return {
    attendance_student_swipes: ui.studentSwipes,
    attendance_absentee_alerts: ui.absenteeAlerts,
    attendance_sms_preference: smsPref || 1,
    attendance_whatsapp_preference: waPref || 1,
    auto_sms_in: ui.autoSmsIn,
    auto_sms_out: ui.autoSmsOut,
    auto_sms_preference: autoSmsPref || 1,
    auto_whatsapp_preference: autoWaPref || 1,
    auto_sms_min_gap: parseInt(ui.autoSmsMinGap, 10) || 5,
    mis_absentee_days: parseInt(ui.misAbsenteeDays, 10) || 3,
  }
}

export default function useAttendanceSettings() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  const loadSettings = useCallback(async () => {
    try {
      setIsLoading(true)
      const { data } = await api.get('/attendance/settings')
      if (data) {
        setSettings(backendToUi(data))
      }
    } catch (e) {
      console.error('Failed to load attendance settings', e)
      appToast.error('Failed to load attendance settings.')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  const setField = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const toggleAbsenteeSms = (role) => {
    setSettings((prev) => ({
      ...prev,
      absenteeSmsTo: {
        ...prev.absenteeSmsTo,
        [role]: !prev.absenteeSmsTo[role],
      },
    }))
  }

  const toggleAbsenteeWhatsApp = (role) => {
    setSettings((prev) => ({
      ...prev,
      absenteeWhatsAppTo: {
        ...prev.absenteeWhatsAppTo,
        [role]: !prev.absenteeWhatsAppTo[role],
      },
    }))
  }

  const toggleAutoSms = (role) => {
    setSettings((prev) => ({
      ...prev,
      autoSmsTo: {
        ...prev.autoSmsTo,
        [role]: !prev.autoSmsTo[role],
      },
    }))
  }

  const toggleAutoWhatsApp = (role) => {
    setSettings((prev) => ({
      ...prev,
      autoWhatsAppTo: {
        ...prev.autoWhatsAppTo,
        [role]: !prev.autoWhatsAppTo[role],
      },
    }))
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const payload = uiToBackend(settings)
      const { data } = await api.put('/attendance/settings', payload)
      if (data) {
        setSettings(backendToUi(data))
      }
      appToast.success('Attendance automation settings saved successfully.')
    } catch (e) {
      console.error('Failed to save attendance settings', e)
      appToast.error(e?.response?.data?.detail || 'Failed to save settings.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleReset = async () => {
    setSettings(DEFAULT_SETTINGS)
    await handleSave()
    appToast.info('Settings have been reset to system defaults.')
  }

  const handleExportAbsenteeExcel = () => {
    appToast.info(
      `Triggering export for students absent for more than ${settings.misAbsenteeDays} consecutive days...`
    )
  }

  return {
    settings,
    isLoading,
    isSaving,
    setField,
    toggleAbsenteeSms,
    toggleAbsenteeWhatsApp,
    toggleAutoSms,
    toggleAutoWhatsApp,
    handleSave,
    handleReset,
    handleExportAbsenteeExcel,
  }
}
